import type { Timestamp } from 'firebase/firestore';

// ============================================
// METRIC TYPES
// ============================================

/**
 * All available metrics that can be used in donation trigger conditions.
 */
export type MetricType =
  | 'checkInsThisWeek'
  | 'checkInsTotal'
  | 'currentStreak'
  | 'messagesThisDay'
  | 'messagesThisWeek'
  | 'reactionsGivenThisDay'
  | 'reactionsReceivedThisDay'
  | 'sessionMinutes'
  | 'consecutiveActiveDays'
  | 'prayersAddedTotal'
  | 'badgesUnlocked'
  | 'prayerDonationClicksThisWeek'
  // Reading engagement metrics
  | 'readingTimeThisDay'
  | 'readingTimeThisWeek'
  | 'versesReadThisDay'
  | 'versesReadThisWeek'
  | 'resourcesViewedThisWeek'
  | 'parshasCompletedTotal'
  | 'currentReadingStreak'
  // Prayer hold engagement metrics
  | 'prayerHoldsThisDay'
  | 'prayerHoldsThisWeek'
  | 'prayerHoldTimeThisDay'
  | 'prayerHoldTimeThisWeek'
  | 'prayerHoldsTotal'
  | 'prayerHoldTimeTotal'
  | 'healingPrayerHolds'
  | 'memoryPrayerHolds'
  | 'dedicationPrayerHolds';

/**
 * Operators for trigger conditions.
 */
export type ConditionOperator = 'gte' | 'lte' | 'eq' | 'between';

/**
 * Time windows for metric evaluation.
 */
export type TimeWindow = 'session' | 'day' | 'week' | 'month' | 'allTime';

// ============================================
// TRIGGER CONDITIONS
// ============================================

/**
 * A single condition that must be met for a trigger to fire.
 */
export interface TriggerCondition {
  metric: MetricType;
  operator: ConditionOperator;
  value: number;
  maxValue?: number; // For 'between' operator
  timeWindow?: TimeWindow;
}

/**
 * A donation trigger that can be configured by admins.
 */
export interface DonationTrigger {
  id: string;
  name: string;
  enabled: boolean;
  priority: number; // Higher = shown first if multiple match

  conditions: TriggerCondition[];
  conditionLogic: 'AND' | 'OR'; // How conditions combine

  message: string; // Tooltip text, supports {variables}
  icon: string; // Emoji or icon name

  clickAction?: DonationClickAction; // Action when user clicks the badge
}

// ============================================
// CLICK ACTION TYPES
// ============================================

/**
 * Action types for when user clicks the donation badge.
 */
export type DonationActionType =
  | 'external_link' // Opens URL in new tab
  | 'internal_content' // Navigate to or preview app content
  | 'full_message'; // Show rich donation modal with page builder content

/**
 * Internal content types that can be linked.
 */
export type InternalContentType = 'material' | 'post' | 'prayer';

/**
 * Block types for the donation page builder.
 */
export type DonationBlockType = 'image' | 'text' | 'button';

/**
 * A block in the donation page builder.
 */
export interface DonationBlock {
  id: string;
  type: DonationBlockType;
  order: number;
  // Image block fields
  imageUrl?: string;
  imageAlt?: string;
  imageLinkUrl?: string; // Optional: click image to open donation link
  imageStoragePath?: string; // Firebase storage path for uploaded images
  // Text block fields
  textContent?: string;
  // Button block fields
  buttonText?: string;
  buttonUrl?: string;
}

/**
 * Configuration for what happens when a user clicks the donation badge.
 */
export interface DonationClickAction {
  type: DonationActionType;
  // For external_link
  externalUrl?: string;
  // For internal_content
  contentType?: InternalContentType;
  contentId?: string;
  contentTitle?: string; // Cached title for display in admin
  showPreview?: boolean; // true = show preview modal, false = navigate directly
  // For full_message - either reference a template or use inline blocks
  templateId?: string; // Reference to a saved template
  templateName?: string; // Cached template name for display
  blocks?: DonationBlock[]; // Inline blocks (used if no templateId)
}

// ============================================
// DONATION TEMPLATES
// ============================================

/**
 * A reusable donation page template.
 * Path: artifacts/{appId}/donation_templates/{templateId}
 */
export interface DonationTemplate {
  id: string;
  name: string;
  description?: string;
  blocks: DonationBlock[];
  createdAt?: Timestamp;
  createdBy?: string;
  updatedAt?: Timestamp;
  updatedBy?: string;
}

// ============================================
// DONATION CONFIGURATION
// ============================================

/**
 * Display position for the donation badge.
 */
export type DonationDisplayPosition = 'top-right' | 'bottom-right' | 'header-inline';

/**
 * Animation style for the donation badge.
 */
export type DonationAnimationStyle = 'pulse' | 'bounce' | 'glow' | 'none';

/**
 * Display settings for the donation badge.
 */
export interface DonationDisplaySettings {
  position: DonationDisplayPosition;
  animationStyle: DonationAnimationStyle;
  autoDismissSeconds: number | null; // null = no auto-dismiss
}

/**
 * Main donation configuration stored in Firebase.
 * Path: artifacts/{appId}/config/donation
 */
export interface DonationConfig {
  enabled: boolean;
  globalCooldownHours: number; // Min hours between prompts (e.g., 24)
  dismissCooldownHours: number; // Cooldown after user dismisses (e.g., 72)
  maxPromptsPerWeek: number; // Rate limit (e.g., 3)

  triggers: DonationTrigger[];

  display: DonationDisplaySettings;

  updatedAt?: Timestamp;
  updatedBy?: string;
}

// ============================================
// USER ENGAGEMENT METRICS
// ============================================

/**
 * User engagement metrics tracked for donation triggers.
 * Path: artifacts/{appId}/users/{userId}/metrics/engagement
 */
export interface UserEngagementMetrics {
  // Rolling counters (updated on each action)
  messagesThisDay: number;
  messagesThisWeek: number;
  reactionsGivenThisDay: number;
  reactionsReceivedThisDay: number;
  checkInsThisWeek: number;
  prayerDonationClicksThisWeek: number;

  // Session tracking
  currentSessionStart: Timestamp | null;
  sessionMinutesToday: number;

  // Streak tracking (complements existing UserStats)
  consecutiveActiveDays: number;
  lastActiveDate: string; // YYYY-MM-DD for day comparison

  // Reset tracking
  dayResetDate: string; // YYYY-MM-DD
  weekResetDate: string; // YYYY-MM-DD (Monday)

  // Reading engagement metrics
  readingTimeThisDay: number; // Minutes reading Torah text today
  readingTimeThisWeek: number; // Minutes reading Torah text this week
  versesReadThisDay: number; // Verses read today
  versesReadThisWeek: number; // Verses read this week
  resourcesViewedThisWeek: number; // Resources opened this week
  parshasCompletedTotal: number; // Lifetime parshas completed
  currentReadingStreak: number; // Reading streak days

  // Prayer hold engagement metrics
  prayerHoldsThisDay: number; // Prayer holds today
  prayerHoldsThisWeek: number; // Prayer holds this week
  prayerHoldTimeThisDay: number; // Total seconds held today
  prayerHoldTimeThisWeek: number; // Total seconds held this week
  prayerHoldsTotal: number; // Lifetime prayer hold count
  prayerHoldTimeTotal: number; // Lifetime total seconds in prayer holds
  longestPrayerHoldSec: number; // Personal record (seconds)
  healingPrayerHolds: number; // Lifetime healing prayer holds
  memoryPrayerHolds: number; // Lifetime memory prayer holds
  dedicationPrayerHolds: number; // Lifetime dedication prayer holds

  updatedAt?: Timestamp;
}

// ============================================
// DONATION PROMPT HISTORY
// ============================================

/**
 * Action taken on a donation prompt.
 */
export type DonationPromptAction = 'clicked' | 'dismissed' | 'expired';

/**
 * Record of a donation prompt shown to a user.
 * Path: artifacts/{appId}/users/{userId}/donation_prompts/{promptId}
 */
export interface DonationPromptRecord {
  id?: string; // Document ID (set when reading from Firestore)
  triggerId: string;
  triggerName: string;
  shownAt: Timestamp;
  action: DonationPromptAction | null; // null = still showing/pending
  actionAt?: Timestamp;
  // For manual prompts, store the full display data
  message?: string;
  icon?: string;
  isManual?: boolean; // Flag for manually sent prompts
  // Click action override (for manual sends with custom action)
  clickAction?: DonationClickAction;
}

// ============================================
// DEFAULT VALUES
// ============================================

/**
 * Default donation configuration for new installs.
 */
export const DEFAULT_DONATION_CONFIG: Omit<DonationConfig, 'updatedAt' | 'updatedBy'> = {
  enabled: false,
  globalCooldownHours: 24,
  dismissCooldownHours: 72,
  maxPromptsPerWeek: 3,
  triggers: [],
  display: {
    position: 'bottom-right',
    animationStyle: 'pulse',
    autoDismissSeconds: null,
  },
};

/**
 * Default user engagement metrics for new users.
 */
export const DEFAULT_ENGAGEMENT_METRICS: Omit<UserEngagementMetrics, 'updatedAt'> = {
  messagesThisDay: 0,
  messagesThisWeek: 0,
  reactionsGivenThisDay: 0,
  reactionsReceivedThisDay: 0,
  checkInsThisWeek: 0,
  prayerDonationClicksThisWeek: 0,
  currentSessionStart: null,
  sessionMinutesToday: 0,
  consecutiveActiveDays: 0,
  lastActiveDate: '',
  dayResetDate: '',
  weekResetDate: '',
  // Reading engagement metrics
  readingTimeThisDay: 0,
  readingTimeThisWeek: 0,
  versesReadThisDay: 0,
  versesReadThisWeek: 0,
  resourcesViewedThisWeek: 0,
  parshasCompletedTotal: 0,
  currentReadingStreak: 0,
  // Prayer hold engagement metrics
  prayerHoldsThisDay: 0,
  prayerHoldsThisWeek: 0,
  prayerHoldTimeThisDay: 0,
  prayerHoldTimeThisWeek: 0,
  prayerHoldsTotal: 0,
  prayerHoldTimeTotal: 0,
  longestPrayerHoldSec: 0,
  healingPrayerHolds: 0,
  memoryPrayerHolds: 0,
  dedicationPrayerHolds: 0,
};

// ============================================
// CONTEXT TYPES
// ============================================

/**
 * Active prompt state in the DonationContext.
 */
export interface ActiveDonationPrompt {
  trigger: DonationTrigger;
  promptId: string;
}

/**
 * Value provided by DonationContext.
 */
export interface DonationContextValue {
  // Current active prompt (null if none)
  activePrompt: ActiveDonationPrompt | null;

  // Actions
  dismissPrompt: () => void;
  clickPrompt: () => void; // Opens donation modal

  // State
  isLoading: boolean;
  config: DonationConfig | null;

  // Click action computed from prompt override or trigger default
  effectiveClickAction: DonationClickAction | null;
}

// ============================================
// HELPER FUNCTIONS
// ============================================

/**
 * Create a new empty block with a unique ID.
 */
export function createEmptyBlock(type: DonationBlockType, order: number): DonationBlock {
  return {
    id: `block_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`, // eslint-disable-line sonarjs/pseudo-random -- non-security ID for UI block ordering
    type,
    order,
  };
}

/**
 * Default click action (none configured).
 */
export const DEFAULT_CLICK_ACTION: DonationClickAction = {
  type: 'external_link',
  externalUrl: '',
};
